import { useEffect, useState } from 'react';
import TagManager from 'react-gtm-module';
import { connect, ConnectedProps } from 'react-redux';
import { useLocation, useNavigate } from 'react-router';

import lang from '../../../lib/language';
import { ENROLL_REQUEST_STATE } from '../../../lib/models/enroll';
import {
  ENROLL_AUTH_REQUEST_TYPE,
  REQUEST_CALL_NAME_CONFIRM_EMAIL,
  REQUEST_CALL_NAME_REDEEM_SIGNUP_TOKEN,
} from '../../../lib/models/enroll-requests';
import { RootState } from '../../../lib/store';
import { enrollAuth } from '../../../lib/store/auth';
import { login as serviceLogin } from '../../../lib/store/common-actions';
import {
  setInitiallySelectedAutoPass as setInitiallySelectedAutoPassCall,
  setInitiallySelectedPlanId as setInitiallySelectedPlanIdCall,
  setInitiallySelectedReferralCode as setInitiallySelectedReferralCodeCall,
} from '../../../lib/store/crm';
import { parseOnboardingErrorCode } from '../../../lib/store/crm/helpers';
import { NullableNumber, NullableString } from '../../../lib/util/types';

import EmailConfirmedModal from '../../../components/Enroll/EmailConfirmedModal/EmailConfirmedModal';
import ExpiredVerificationModal from '../../../components/Enroll/ExpiredVerificationModal/ExpiredVerificationModal';
import VerifyEmailModal from '../../../components/Enroll/VerifyEmailModal/VerifyEmailModal';
import { GTM_CATEGORIES, GTM_CLIENT_CREATION_STEPS, GTM_EVENTS } from '../../../constants/gtm';
import EnrollmentState from '../../../store-util/EnrollmentState';
import { hashSHA256 } from '../../../util/hash';
import { getRedeemSignupTokenParams, hasConfirmEmailToken } from '../../../util/redeemSignupTokenUrl';
import { UI_ROUTES } from '../../../util/routes';
import Layout from '../component/Layout';

import styles from './EnrollVerifyEmail.module.scss';

type Props = ReduxProps;

const EnrollVerifyEmail = (props: Props): JSX.Element => {
  const {
    isAppLoaded,
    responseStatus,
    responseStatusCode,
    requestCallName,
    tradingPlatformId,
    authenticate,
    redeemSignupToken,
    setInitiallySelectedPlanId,
    initiallySelectedPlanId,
    setInitiallySelectedAutoPass,
    setInitiallySelectedReferralCode,
    userId,
  } = props;

  const location = useLocation();
  const navigate = useNavigate();
  const { form } = EnrollmentState.use();
  const [isVerifyLinkExpired, setIsVerifyLinkExpired] = useState<boolean>(false);
  const [verifyEmailModalOpened, setVerifyEmailModalOpened] = useState<boolean>(true);
  const [emailConfirmedModal, setEmailConfirmedModal] = useState<boolean>(false);
  const isConfirmURL = hasConfirmEmailToken(location.search) || !!getRedeemSignupTokenParams(location.search);

  const checkAndRedirectToPreviousScreen = (): void => {
    if (!form.steps.isAuthInfoPassed) {
      navigate(UI_ROUTES.secondEnroll, { replace: true });
    }
  };

  useEffect(() => {
    if (!isAppLoaded) {
      return;
    }

    if (isConfirmURL) {
      const queryParams = new URLSearchParams(location.search);
      const redeemParams = getRedeemSignupTokenParams(location.search);

      if (queryParams) {
        const withAutoPass = queryParams.get('AutoPass') === 'True';
        const tradingPlanId = Number(queryParams.get('TradingPlanId'));
        if (tradingPlanId && tradingPlanId !== initiallySelectedPlanId) {
          setInitiallySelectedPlanId(tradingPlanId);
        }
        if (withAutoPass !== undefined) {
          setInitiallySelectedAutoPass(withAutoPass);
        }

        // The referral program code travels back from the invitation link through the
        // confirmation email, because the individual - and with it the attribution - is only
        // created once the email is confirmed, possibly in a different browser session.
        const referralCode = queryParams.get('Referral');
        if (referralCode) {
          setInitiallySelectedReferralCode(referralCode);
        }

        if (redeemParams) {
          redeemSignupToken(
            redeemParams.username,
            redeemParams.signupToken,
            redeemParams.returnUrl,
            queryParams.get('FirstName'),
            queryParams.get('LastName'),
            queryParams.get('CountryOfResidence'),
            queryParams.get('clientId'),
          );
        } else {
          authenticate(
            queryParams.get('Email'),
            queryParams.get('token'),
            queryParams.get('FirstName'),
            queryParams.get('LastName'),
            queryParams.get('CountryOfResidence'),
            queryParams.get('clientId'),
          );
        }
      } else {
        checkAndRedirectToPreviousScreen();
      }
    } else {
      checkAndRedirectToPreviousScreen();
    }
  }, [isAppLoaded, authenticate, redeemSignupToken, location.search, form.steps.isAuthInfoPassed]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // todo: should we check for cookie here ?
    if (tradingPlatformId) {
      setEmailConfirmedModal(true);
    }
  }, [tradingPlatformId]);

  useEffect(() => {
    if (
      responseStatus
      && responseStatus === ENROLL_REQUEST_STATE.ERROR
      && parseOnboardingErrorCode(responseStatusCode!) === ENROLL_REQUEST_STATE.REQUEST_ERROR
      && (
        requestCallName === REQUEST_CALL_NAME_CONFIRM_EMAIL
        || requestCallName === REQUEST_CALL_NAME_REDEEM_SIGNUP_TOKEN
      )
    ) {
      setIsVerifyLinkExpired(true);

      // Push GTM event for email verification link expired
      const pushGtmEvent = async () => {
        const hashedEmail = await hashSHA256(form.email);
        TagManager.dataLayer({
          dataLayer: {
            event: GTM_EVENTS.CLIENT_CREATION,
            event_step: GTM_CLIENT_CREATION_STEPS.STEP_4_ERROR.step,
            event_category: GTM_CATEGORIES.CLIENT_CREATION_FUNNEL,
            event_action: GTM_CLIENT_CREATION_STEPS.STEP_4_ERROR.action,
            event_label: GTM_CLIENT_CREATION_STEPS.STEP_4_ERROR.label,
            client_id: userId,
            user_email_hashed: hashedEmail,
          },
        });
      };
      pushGtmEvent();
    }
  }, [requestCallName, responseStatus, responseStatusCode, form.email, userId]);

  const isEmailVerifying = verifyEmailModalOpened && !emailConfirmedModal;

  return (
    <Layout
      transparentContent
      formTitle={isVerifyLinkExpired ? lang.commonOnboardingExpiredVerificationLinkTitle() : ''}
      contentStyle={styles.verificationContent}
      displayBackButton={false}
      displayFooter={false}
      displayNextButton={!isEmailVerifying}
      nextButtonText={lang.commonContinueButton()}
      displayLoginButton={false}
      onSubmit={() => {
        EnrollmentState.setVerifyEmailStepPassed(true);
        navigate(UI_ROUTES.enrollPhoneVerification);
      }}
    >
      <VerifyEmailModal
        largeText
        noModalDisplay
        userEmail={form.email}
        handleCloseVerifyEmailModal={() => setVerifyEmailModalOpened(false)}
        forceNoRender={isConfirmURL || isVerifyLinkExpired || !isEmailVerifying}
      />
      <EmailConfirmedModal
        isOpened={!isVerifyLinkExpired && !isEmailVerifying}
        noModalDisplay
        forceNoRender={isVerifyLinkExpired || isEmailVerifying}
      />
      <ExpiredVerificationModal noModalDisplay forceNoRender={!isVerifyLinkExpired} />
    </Layout>
  );
};

const mapDispatchToProps = (dispatch: any) => ({
  login: (): void => {
    dispatch(serviceLogin({ authType: 'login' }));
  },
  setInitiallySelectedPlanId: (planId: NullableNumber) =>
    dispatch(setInitiallySelectedPlanIdCall(planId)),
  setInitiallySelectedAutoPass: (autoPass: boolean) =>
    dispatch(setInitiallySelectedAutoPassCall(autoPass)),
  setInitiallySelectedReferralCode: (referralCode: NullableString) =>
    dispatch(setInitiallySelectedReferralCodeCall(referralCode)),
  authenticate: (
    email: NullableString,
    token: NullableString,
    firstName: NullableString,
    lastName: NullableString,
    country: NullableString,
    referrerClientId: NullableString,
  ): void => {
    dispatch(
      enrollAuth({
        requestType: ENROLL_AUTH_REQUEST_TYPE.CONFIRM_EMAIL,
        email,
        token,
        firstName,
        lastName,
        country,
        referrerClientId,
      }),
    );
  },
  redeemSignupToken: (
    username: string,
    signupToken: string,
    returnUrl: string | undefined,
    firstName: NullableString,
    lastName: NullableString,
    country: NullableString,
    referrerClientId: NullableString,
  ): void => {
    dispatch(enrollAuth({
      requestType: ENROLL_AUTH_REQUEST_TYPE.REDEEM_SIGNUP_TOKEN,
      username,
      signupToken,
      returnUrl,
      firstName,
      lastName,
      country,
      referrerClientId,
    }));
  },
});

const mapStateToProps = (state: RootState) => ({
  isAppLoaded: state.app.loaded,
  responseStatus: state.auth.enroll?.status,
  responseStatusCode: state.auth.enroll?.statusCode,
  requestCallName: state.auth.enroll?.requestCallName,
  tradingPlatformId: state.crm.tradingPlatformId,
  initiallySelectedPlanId: state.crm.enroll.initiallySelectedPlanId,
  userId: state.auth.enroll.userId,
});
const connector = connect(mapStateToProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;

export default connector(EnrollVerifyEmail);
